// What the three planning pages (Forecast, Purchasing plan, Make plan) draw the same way: an item's detail beside the
// list (`?item=`), and the Why button that explains a planned item.
import { Info, X } from "lucide-react";
import type { ReactNode } from "react";
import type { Item } from "../../../shared/db.gen.ts";
import { explain, type ItemPlanResult, type Plan } from "../../../shared/planning.ts";
import { setSearch } from "../lib/router.tsx";
import { cx, Thumb } from "../ui/controls.tsx";
import { Popover } from "../ui/Popover.tsx";
import { PaneLink } from "../ui/SidePane.tsx";
import { itemPath } from "./ItemsPage.tsx";

/**
 * An item's detail beside a plan: its photo and names, `about` after its SKU (its vendor), Open item and `links`, a ×
 * that closes it (Esc too: useDetailKeys), and its sections. `className` sets its width.
 */
export function PlanDetail({
  item,
  about,
  links,
  className,
  children,
}: {
  item: Item;
  about?: string | null;
  links?: ReactNode;
  className: string;
  children: ReactNode;
}) {
  return (
    <aside className={cx("flex h-full shrink-0 flex-col border-l border-line bg-surface", className)}>
      <div className="flex items-start gap-3 border-b border-line px-4 py-3">
        <Thumb src={item.photo_url} size={44} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold">{item.shopify_variant_title ? item.shopify_product_title : item.name}</div>
          <div className="truncate text-muted">{[item.shopify_variant_title, item.sku, about].filter(Boolean).join(" · ")}</div>
          <div className="mt-1 flex gap-3 text-[12px]">
            <PaneLink kind="item" id={item.id} to={itemPath(item)} className="text-accent-text hover:underline">
              Open item
            </PaneLink>
            {links}
          </div>
        </div>
        <button type="button" onClick={() => setSearch({ item: null })} className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="Close (Esc)">
          <X className="size-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">{children}</div>
    </aside>
  );
}

/** The ⓘ by a planned item: its SKU (and `badges`), the plan's reasons step by step, and its flags. */
export function WhyButton({ r, plan, badges, width = 440 }: { r: ItemPlanResult; plan: Plan; badges?: ReactNode; width?: number }) {
  return (
    <Popover
      width={width}
      button={(toggle, open) => (
        <button type="button" title="Why" onClick={toggle} className={cx("rounded p-1 hover:bg-hover hover:text-text", open ? "text-text" : "text-faint")}>
          <Info className="size-3.5" />
        </button>
      )}
    >
      {() => (
        <div className="text-[12px]">
          <div className="mb-1.5 flex items-center gap-2">
            <span className="font-mono font-medium">{r.item.sku}</span>
            {badges}
          </div>
          <ol className="list-decimal space-y-1 pl-4 text-muted">
            {explain(r, plan).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ol>
          {r.flags.length > 0 && (
            <ul className="mt-2 rounded-md bg-warn-soft px-2.5 py-1.5 text-warn">
              {r.flags.map((f) => (
                <li key={f.code}>{f.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Popover>
  );
}
